/**
 * TeamPage — S4: Listagem "Equipe" do Gestor (rota canônica /manager/staff).
 *
 * Equipe = quem trabalha no clube, qual função exerce, por quem é responsável
 * e quantos atletas efetivamente consegue acessar.
 *
 * Fonte: GET /api/gestor/staff (S3) — accessible_count vem da VIEW CANÔNICA
 * (vw_staff_accessible_athletes) via backend; NUNCA recalculado no frontend.
 *
 * Vocabulário visível: "Equipe" / "Profissionais" / "Adicionar profissional".
 * "Staff" fica apenas em código interno. Visual preto/branco/cinza (padrão
 * G2-UX.1): cor só com significado; badges discretos.
 *
 * Cadastro reutiliza POST /roster/staff (mesmo contrato da RosterPage — o
 * backend deriva a instituição server-side; institution_id é apenas hint).
 * Editor de responsabilidades é S5 — esta página só lista e navega.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AZURE } from '../../services/api';
import { useActiveContext } from '../../contexts/ActiveContextContext';
import CategoriesPage from '../CategoriesPage';
import ProfessionalForm from '../../components/staff/ProfessionalForm';

const INK = '#111827';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';
const BG = '#f8fafc';

const ROLE_LABELS = {
  TREINADOR: 'Treinador',
  PREPARADOR_FISICO: 'Prep. Físico',
  NUTRICIONISTA: 'Nutricionista',
  FISIOTERAPEUTA: 'Fisioterapeuta',
  MEDICO: 'Médico',
  ANALISTA: 'Analista',
  AUXILIAR: 'Auxiliar',
  GESTOR: 'Gestor',
};
const ROLES = ['TREINADOR', 'PREPARADOR_FISICO', 'NUTRICIONISTA',
  'FISIOTERAPEUTA', 'MEDICO', 'ANALISTA'];

/** Modal de cadastro — P2.4a: casca do modal + ProfessionalForm canônico.
 *  O modal é dono do endpoint (POST /roster/staff); o form não faz HTTP. */
const AddProfessionalModal = ({ open, onClose, clubId, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // INV-4b fluxo único: após create com convite o modal NÃO fecha — mostra
  // resultado (e-mail enviado ou não) + Copiar/WhatsApp, e só sai no Concluir.
  // token vive apenas neste estado em memória (nunca em listas/storage).
  const [inviteResult, setInviteResult] = useState(null);
  // {email, sent, inviteUrl|null, staffId}
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  if (!open) return null;

  const close = () => {
    setError(null);
    setInviteResult(null);
    setCopied(false);
    onClose();
  };

  const submit = async (values) => {
    setSaving(true); setError(null);
    try {
      const { data } = await AZURE.post('/roster/staff',
        { ...values, institution_id: clubId });
      onSuccess();
      if (values.send_invite) {
        const token = data?.invite?.invite_token || null;
        setInviteResult({
          email: values.email,
          sent: data?.delivery?.email?.sent === true,
          inviteUrl: token
            ? `${window.location.origin}/accept-invite?token=${token}`
            : null,
          staffId: data?.data?.staff_id,
        });
        return; // fecha só no Concluir
      }
      close();
    } catch (e) {
      setError(e?.response?.data?.error || 'Erro ao cadastrar profissional');
    } finally { setSaving(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteResult.inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard indisponível — sem crash */ }
  };

  const whatsappHref = inviteResult?.inviteUrl
    ? `https://wa.me/?text=${encodeURIComponent(
        `Você foi convidado(a) para a VUKZ AI. Acesse: ${inviteResult.inviteUrl}`)}`
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={inviteResult ? undefined : close}>
      <div className="bg-white rounded-xl border w-full max-w-md p-6"
        style={{ borderColor: BORDER }} onClick={(e) => e.stopPropagation()}>
        {inviteResult ? (
          <div>
            <h2 className="text-lg font-black mb-2" style={{ color: INK }}>
              ✓ Profissional cadastrado
            </h2>
            {inviteResult.sent ? (
              <div className="border rounded-lg p-3 mb-4 text-sm"
                style={{ borderColor: '#86efac', backgroundColor: '#f0fdf4', color: '#166534' }}>
                Convite enviado para <strong>{inviteResult.email}</strong>.
              </div>
            ) : (
              <div className="border rounded-lg p-3 mb-4 text-sm"
                style={{ borderColor: '#fde047', backgroundColor: '#fefce8', color: '#854d0e' }}>
                ⚠ Não foi possível enviar o e-mail para{' '}
                <strong>{inviteResult.email}</strong>.
                {inviteResult.inviteUrl
                  ? ' Envie o link por outro canal abaixo.'
                  : ' Abra a ficha do profissional para gerar o convite.'}
              </div>
            )}
            {inviteResult.inviteUrl ? (
              <div className="flex gap-2 mb-3">
                <button onClick={copyLink}
                  className="flex-1 py-2 border rounded-lg text-sm font-bold"
                  style={{ borderColor: BORDER, color: INK }}>
                  {copied ? '✓ Copiado' : 'Copiar link'}
                </button>
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
                  className="flex-1 py-2 border rounded-lg text-sm font-bold text-center"
                  style={{ borderColor: '#86efac', color: '#166534' }}>
                  WhatsApp
                </a>
              </div>
            ) : (
              <button
                onClick={() => {
                  const id = inviteResult.staffId;
                  close();
                  if (id) navigate(`/manager/staff/${id}`);
                }}
                className="w-full py-2 border rounded-lg text-sm font-bold mb-3"
                style={{ borderColor: BORDER, color: INK }}>
                Abrir ficha
              </button>
            )}
            <button onClick={close}
              className="w-full py-2 rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: INK }}>
              Concluir
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-lg font-black mb-4" style={{ color: INK }}>
              Adicionar profissional
            </h2>
            <ProfessionalForm
              mode="create"
              onSubmit={submit}
              onCancel={close}
              loading={saving}
              error={error}
            />
          </>
        )}
      </div>
    </div>
  );
};

const TeamPage = () => {
  const navigate = useNavigate();
  const { clubId } = useActiveContext();
  // E1: tab via ?tab= — deep-linkável; default 'profissionais'
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'categorias' ? 'categorias' : 'profissionais';
  const setTab = (t) => setSearchParams(t === 'profissionais' ? {} : { tab: t }, { replace: true });
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  // INV-4: segmentação por estado de ACESSO (account_state do read model).
  // 'all' inclui NO_ACCOUNT (legados) — nunca escondemos profissionais antigos.
  const [accessFilter, setAccessFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await AZURE.get('/gestor/staff');
      setStaff(data?.data?.staff || []);
    } catch (e) {
      // Erro de API NÃO vira lista vazia silenciosa
      setError(e?.response?.data?.error || 'Erro ao carregar a equipe');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = staff.filter((s) => {
    if (roleFilter && s.staff_role !== roleFilter) return false;
    if (statusFilter === 'active' && !s.is_active) return false;
    if (statusFilter === 'inactive' && s.is_active) return false;
    if (accessFilter === 'account' && s.account_state !== 'ACCOUNT_ACTIVE') return false;
    if (accessFilter === 'invited' && s.account_state !== 'INVITE_PENDING') return false;
    if (q) {
      const t = q.toLowerCase();
      if (!(s.full_name || '').toLowerCase().includes(t)
        && !(s.email || '').toLowerCase().includes(t)) return false;
    }
    return true;
  });

  const responsibilityText = (s) => {
    if (s.responsibility_summary?.length) {
      const shown = s.responsibility_summary.slice(0, 2).join(' · ');
      const extra = s.active_rules - s.responsibility_summary.length;
      return extra > 0 ? `${shown} +${extra}` : shown;
    }
    // Sem regras: acesso pode vir de vínculos legados
    if (s.accessible_count > 0) return 'Vínculos diretos';
    return '—';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: BG }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        {/* Título — header/logo vêm do Layout padrão */}
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-black tracking-tight" style={{ color: INK }}>Equipe</h1>
          {tab === 'profissionais' && (
            <button onClick={() => setModalOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white hover:opacity-90"
              style={{ backgroundColor: INK }}>
              + Adicionar profissional
            </button>
          )}
        </div>
        <p className="text-sm mb-4" style={{ color: MUTED }}>
          Estrutura, profissionais e responsabilidades do clube
        </p>

        {/* E1: tabs Profissionais | Categorias — mesma pergunta ("como minha
            equipe está organizada?"), fontes de dados separadas no backend. */}
        <div className="flex gap-1 border-b mb-5" style={{ borderColor: BORDER }}>
          {[['profissionais', 'Profissionais'], ['categorias', 'Categorias']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className="px-4 py-2 text-sm font-bold -mb-px border-b-2 transition-colors"
              style={tab === key
                ? { color: INK, borderColor: INK }
                : { color: MUTED, borderColor: 'transparent' }}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'categorias' ? (
          <CategoriesPage embedded />
        ) : (
          <>

        {/* Card único de filtros */}
        <div className="bg-white rounded-xl border p-4 mb-4" style={{ borderColor: BORDER }}>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: MUTED }}>Buscar</label>
              <input placeholder="Buscar profissional…"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{ borderColor: BORDER, color: INK }}
                value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: MUTED }}>Função</label>
              <select className="px-3 py-2 border rounded-lg text-sm bg-white"
                style={{ borderColor: BORDER, color: INK }}
                value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="">Todas</option>
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: MUTED }}>Status</label>
              <select className="px-3 py-2 border rounded-lg text-sm bg-white"
                style={{ borderColor: BORDER, color: INK }}
                value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="active">Ativos</option>
                <option value="inactive">Inativos</option>
                <option value="">Todos</option>
              </select>
            </div>
            <span className="text-xs pb-2 ml-auto" style={{ color: MUTED }}>
              {filtered.length} {filtered.length === 1 ? 'profissional' : 'profissionais'}
            </span>
          </div>
        </div>

        {error && (
          <div className="bg-white border rounded-xl p-4 mb-4 flex items-center justify-between"
            style={{ borderColor: '#fca5a5' }}>
            <span className="text-sm" style={{ color: '#b91c1c' }}>{error}</span>
            <button onClick={load}
              className="px-3 py-1.5 border rounded-lg text-xs font-bold"
              style={{ borderColor: BORDER, color: INK }}>
              Tentar novamente
            </button>
          </div>
        )}

        {/* INV-4: segmentação por acesso — Convidados = convite pending */}
        <div className="flex gap-1.5 mb-3">
          {[['all', 'Todos'], ['account', 'Com acesso'], ['invited', 'Convidados']].map(([key, label]) => (
            <button key={key} onClick={() => setAccessFilter(key)}
              className="px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors"
              style={accessFilter === key
                ? { backgroundColor: INK, color: '#fff', borderColor: INK }
                : { backgroundColor: '#fff', color: MUTED, borderColor: BORDER }}>
              {label}
              {key === 'invited' && (() => {
                const n = staff.filter((s) => s.account_state === 'INVITE_PENDING').length;
                return n > 0 ? ` (${n})` : '';
              })()}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white h-14 rounded-xl animate-pulse border"
                style={{ borderColor: BORDER }} />
            ))}
          </div>
        ) : !error && (
          <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: BORDER }}>
            {/* Cabeçalho da tabela */}
            <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 border-b text-[10px] font-black uppercase tracking-widest"
              style={{ borderColor: BORDER, color: MUTED }}>
              <div className="col-span-4">Profissional</div>
              <div className="col-span-2">Função</div>
              <div className="col-span-3">Responsabilidade</div>
              <div className="col-span-1 text-right">Atletas</div>
              <div className="col-span-2 text-right">Status</div>
            </div>

            {filtered.length === 0 ? (
              <div className="p-10 text-center text-sm" style={{ color: MUTED }}>
                {staff.length === 0
                  ? 'Nenhum profissional cadastrado ainda.'
                  : 'Nenhum profissional corresponde aos filtros.'}
              </div>
            ) : filtered.map((s) => (
              <button key={s.staff_id}
                onClick={() => navigate(`/manager/staff/${s.staff_id}`)}
                className="w-full grid grid-cols-2 md:grid-cols-12 gap-3 px-4 py-3 border-b last:border-b-0 text-left hover:bg-gray-50 transition-colors items-center"
                style={{ borderColor: BORDER }}>
                <div className="col-span-2 md:col-span-4 min-w-0">
                  <div className="text-sm font-bold truncate" style={{ color: INK }}>
                    {s.full_name}
                  </div>
                  <div className="text-xs truncate" style={{ color: MUTED }}>
                    {s.email || '—'}
                    {s.account_state === 'INVITE_PENDING' && (
                      <span className="ml-2 px-1.5 py-0.5 rounded border text-[10px] font-semibold"
                        style={{ borderColor: '#fde047', color: '#854d0e', backgroundColor: '#fefce8' }}>
                        ✉ Convite enviado
                        {s.pending_invite_expires_at &&
                          ` · válido até ${new Date(s.pending_invite_expires_at).toLocaleDateString('pt-BR')}`}
                      </span>
                    )}
                    {s.account_state === 'NO_ACCOUNT' && (
                      <span className="ml-2 px-1.5 py-0.5 rounded border text-[10px] font-semibold"
                        style={{ borderColor: BORDER, color: MUTED }}>
                        Sem acesso à plataforma
                      </span>
                    )}
                  </div>
                </div>
                <div className="md:col-span-2 text-sm" style={{ color: INK }}>
                  {ROLE_LABELS[s.staff_role] || s.staff_role}
                </div>
                <div className="md:col-span-3 text-sm truncate" style={{ color: s.active_rules > 0 ? INK : MUTED }}>
                  {responsibilityText(s)}
                </div>
                <div className="md:col-span-1 text-sm font-bold md:text-right" style={{ color: INK }}>
                  {s.accessible_count}
                </div>
                <div className="md:col-span-2 md:text-right">
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border"
                    style={s.is_active
                      ? { borderColor: '#a7f3d0', color: '#047857', backgroundColor: '#f0fdf4' }
                      : { borderColor: BORDER, color: MUTED }}>
                    {s.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
          </>
        )}
      </div>

      <AddProfessionalModal open={modalOpen} onClose={() => setModalOpen(false)}
        clubId={clubId} onSuccess={load} />
    </div>
  );
};

export default TeamPage;
